import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Modal, Select, Spinner, Textarea } from '../../components/ui'
import { clp, formatDateShort, QUOTE_STATUS } from '../../utils/format'
import type { PortalBattery, Quote } from '../../types/api'
import { Icon } from '../../components/ui/icons'

interface DraftItem {
  batteryId: string
  quantity: number
}

/** Cotizaciones de la empresa: solicitar, revisar respuesta, aceptar/rechazar. */
export function PortalQuotesPage() {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [items, setItems] = useState<DraftItem[]>([{ batteryId: '', quantity: 1 }])
  const [message, setMessage] = useState('')
  const [detail, setDetail] = useState<Quote | null>(null)

  const quotes = useQuery({
    queryKey: ['portal-quotes'],
    queryFn: async () => (await api.get<Quote[]>('/portal/quotes')).data,
  })
  const batteries = useQuery({
    queryKey: ['portal-batteries'],
    queryFn: async () => (await api.get<PortalBattery[]>('/portal/batteries')).data,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['portal-quotes'] })
    queryClient.invalidateQueries({ queryKey: ['portal-dashboard'] })
  }

  const request = useMutation({
    mutationFn: async () =>
      (
        await api.post('/portal/quotes', {
          items: items.filter((i) => i.batteryId).map((i) => ({ batteryId: i.batteryId, quantity: i.quantity })),
          message: message || null,
        })
      ).data,
    onSuccess: () => {
      setShowNew(false)
      setItems([{ batteryId: '', quantity: 1 }])
      setMessage('')
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const decide = useMutation({
    mutationFn: async ({ id, accept }: { id: string; accept: boolean }) =>
      (await api.post(`/portal/quotes/${id}/${accept ? 'accept' : 'reject'}`, {})).data,
    onSuccess: (quote: Quote) => {
      setDetail(quote)
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const validItems = items.filter((i) => i.batteryId && i.quantity > 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Cotizaciones</h1>
        <Button variant="cta" onClick={() => setShowNew(true)}>+ Solicitar cotización</Button>
      </div>

      <ErrorText>{error}</ErrorText>

      <Card>
        {quotes.isLoading ? (
          <Spinner />
        ) : (quotes.data?.length ?? 0) === 0 ? (
          <EmptyState message="Aún no tienes cotizaciones. Solicita la primera con el botón de arriba." />
        ) : (
          <div className="space-y-2">
            {(quotes.data ?? []).map((q) => (
              <button
                key={q.id}
                onClick={() => setDetail(q)}
                className="block w-full rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-brand-500"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{q.codeLabel}</span>{' '}
                    <span className="text-sm font-medium text-ink">
                      {q.items.map((i) => `${i.quantity}× ${i.battery.name}`).join(' · ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.total != null && <span className="text-sm font-semibold text-ink">{clp(q.total)}</span>}
                    <Badge className={QUOTE_STATUS[q.status].className}>{QUOTE_STATUS[q.status].label}</Badge>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Solicitada el {formatDateShort(q.createdAt)}
                  {q.validUntil ? ` · válida hasta ${formatDateShort(q.validUntil)}` : ''}
                </p>
              </button>
            ))}
          </div>
        )}
      </Card>

      {showNew && (
        <Modal title="Solicitar cotización" onClose={() => setShowNew(false)} wide>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Indica las baterías y cantidades que necesitas. Te responderemos con precios y condiciones al correo y aquí en el portal.
            </p>
            {items.map((item, idx) => (
              <div key={idx} className="flex items-end gap-2">
                <div className="flex-1">
                  <Field label={`Batería ${idx + 1}`}>
                    <Select value={item.batteryId} onChange={(e) => setItems(items.map((it, i) => (i === idx ? { ...it, batteryId: e.target.value } : it)))}>
                      <option value="">Selecciona…</option>
                      {(batteries.data ?? []).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                          {b.jobProfile ? ` — ${b.jobProfile}` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                <div className="w-24">
                  <Field label="Cantidad">
                    <Input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) => setItems(items.map((it, i) => (i === idx ? { ...it, quantity: Number(e.target.value) } : it)))}
                    />
                  </Field>
                </div>
                {items.length > 1 && (
                  <Button variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} aria-label="Quitar">
                    <Icon name="close" className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="secondary" onClick={() => setItems([...items, { batteryId: '', quantity: 1 }])}>
              + Agregar otra batería
            </Button>
            <Field label="Mensaje (opcional)" hint="Cuéntanos de la faena, plazos o cargos específicos">
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
            </Field>
            <ErrorText>{error}</ErrorText>
            <Button variant="cta" className="w-full" disabled={validItems.length === 0 || request.isPending} onClick={() => request.mutate()}>
              {request.isPending ? 'Enviando…' : 'Enviar solicitud'}
            </Button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`Cotización ${detail.codeLabel}`} onClose={() => setDetail(null)} wide>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className={QUOTE_STATUS[detail.status].className}>{QUOTE_STATUS[detail.status].label}</Badge>
              <span className="text-xs text-gray-500">Solicitada el {formatDateShort(detail.createdAt)}</span>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-1.5">Batería</th>
                  <th className="py-1.5">Cantidad</th>
                  <th className="py-1.5">Precio unitario</th>
                  <th className="py-1.5 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detail.items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-2">{i.battery.name}</td>
                    <td className="py-2">{i.quantity}</td>
                    <td className="py-2">{i.unitPrice != null ? clp(i.unitPrice) : 'Por confirmar'}</td>
                    <td className="py-2 text-right">{i.unitPrice != null ? clp(i.unitPrice * i.quantity) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              {detail.total != null && (
                <tfoot>
                  <tr className="border-t border-gray-300 font-semibold text-ink">
                    <td colSpan={3} className="py-2">Total</td>
                    <td className="py-2 text-right">{clp(detail.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>

            {detail.message && (
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
                <span className="font-medium">Tu mensaje:</span> {detail.message}
              </p>
            )}
            {detail.responseNotes && (
              <p className="rounded-lg bg-soft px-3 py-2 text-sm text-ink">
                <span className="font-medium">Condiciones:</span> {detail.responseNotes}
              </p>
            )}
            {detail.validUntil && (
              <p className="text-xs text-gray-500">Oferta válida hasta el {formatDateShort(detail.validUntil)}.</p>
            )}

            <ErrorText>{error}</ErrorText>

            {detail.status === 'RESPONDED' && (
              <div className="flex gap-2">
                <Button variant="cta" className="flex-1" disabled={decide.isPending} onClick={() => decide.mutate({ id: detail.id, accept: true })}>
                  Aceptar cotización
                </Button>
                <Button variant="secondary" className="flex-1" disabled={decide.isPending} onClick={() => decide.mutate({ id: detail.id, accept: false })}>
                  Rechazar
                </Button>
              </div>
            )}
            {detail.status === 'REQUESTED' && (
              <p className="text-sm text-gray-500">Estamos preparando tu cotización; te avisaremos por correo cuando esté respondida.</p>
            )}
            {detail.status === 'ACCEPTED' && (
              <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-ink">
                <Icon name="check" className="h-4 w-4 shrink-0 text-emerald-600" />
                Cotización aceptada. Coordinaremos contigo el agendamiento de los exámenes.
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
